import BigNumber, { BigNumber as BigNumberJs } from 'bignumber.js';
import { expandDecimals } from './numbers';

export function isNullOrBlank(str: string) {
  return !str || /^\s*$/.test(str);
}

export function calculateEarn({
  isLong,
  currenPrice,
  openPrice,
  leverage,
  collateral,
}: {
  isLong: boolean;
  currenPrice: number;
  openPrice: BigNumber;
  leverage: BigNumber;
  collateral: BigNumber;
}): number {
  const open = convertBigNumberToNumber(openPrice, 10);
  const lev = leverage.toNumber();
  const coll = collateral.dividedBy(1e18).toNumber();

  const positionSize = lev * coll;
  const priceDiff = isLong ? currenPrice - open : open - currenPrice;

  const earn = (priceDiff * positionSize) / open;

  return earn;
}

export function calculateFee({
  leverage,
  collateral,
  decimalsToShow,
  decimalTokenUsdc,
  closingFeeP,
}: {
  leverage: BigNumber;
  collateral: BigNumber;
  decimalsToShow: number;
  decimalTokenUsdc: number;
  closingFeeP: BigNumber;
}) {
  return (
    (convertBigNumberToNumber(closingFeeP, 10) *
      leverage.toNumber() *
      convertBigNumberToNumber(collateral, decimalTokenUsdc)) /
    100
  );
}

export function calculateLiquidationPrice({
  isLong,
  fee,
  collateral,
  openPrice,
  leverage,
  decimalsToShow,
  sl,
  decimalTokenUsdc,
}: {
  isLong: boolean;
  fee: number;
  collateral: BigNumber;
  openPrice: BigNumber;
  leverage: BigNumber;
  decimalsToShow: number;
  sl: BigNumber;
  decimalTokenUsdc: number;
}) {
  const liquidPercent = 100;

  const liquiditionPriceDistion =
    (convertBigNumberToNumber(openPrice, 10) *
      ((convertBigNumberToNumber(collateral, decimalTokenUsdc) *
        liquidPercent) /
        100 -
        fee)) /
    convertBigNumberToNumber(collateral, decimalTokenUsdc) /
    leverage.toNumber();
  const liquidationPrice = isLong
    ? convertBigNumberToNumber(openPrice, 10) - liquiditionPriceDistion
    : convertBigNumberToNumber(openPrice, 10) + liquiditionPriceDistion;
  const liq_sl_num = convertBigNumberToNumber(sl, 10);
  let liq_sl = convertBigNumberToNumber(sl, 10).toString();
  if (isLong) {
    if (liq_sl_num < liquidationPrice) {
      liq_sl = `${liquidationPrice.toFixed(decimalsToShow)}`;
    }
  } else {
    if (
      liq_sl_num > liquidationPrice ||
      liq_sl_num < convertBigNumberToNumber(openPrice, 10)
    ) {
      liq_sl = `${liquidationPrice.toFixed(decimalsToShow)}`;
    }
  }
  return { liq_sl, liquidationPrice };
}

export const convertFloatStringToNumber = (
  data: string,
  decimalsToShow: number
) => {
  if (isNullOrBlank(data)) {
    return 0;
  }
  return Number?.(Number?.(data)?.toFixed(decimalsToShow)) || 0;
};

export const convertBigNumberToNumber = (data: BigNumber, decimals: number) => {
  if (!data) return 0;
  if (decimals < 0) {
    return data.toNumber();
  }
  return data.dividedBy(Math.pow(10, decimals)).toNumber();
};

export const convertBigNumberJsToNumber = (value: string, decimals: number) => {
  const numberJs = new BigNumberJs(value).div(10 ** decimals);
  return Number(numberJs);
};

export const convertBigNumberToString = (
  data: BigNumber,
  decimalsToShow: number,
  decimals: number
) => {
  if (decimalsToShow < 0 || decimals < 0) {
    return data.toString();
  }
  return (
    Number(
      data
        .multipliedBy(expandDecimals(1, decimalsToShow))
        .div(expandDecimals(1, decimals))
        .toString()
    ) / Math.pow(10, decimalsToShow)
  ).toString();
};

export function convertValueToNumber(value: number | string, decimals: number) {
  if (!value) {
    return new BigNumber(0).multipliedBy(Math.pow(10, decimals)).toNumber();
  }

  if (typeof value === 'string') {
    value = value.replace(/,/g, '');
  }

  return new BigNumber(value).dividedBy(Math.pow(10, decimals)).toNumber();
}

export function stringToBigNumber(value: number | string, decimals: number) {
  if (!value) {
    return new BigNumber(0).multipliedBy(Math.pow(10, decimals));
  }

  if (typeof value === 'string') {
    value = value.replace(/,/g, '');
  }

  return new BigNumber(value).multipliedBy(Math.pow(10, decimals));
}
